// Deal-detail decline banner. Renders a yellow
// proposal banner while a decline is DeclineProposed and a red ratified
// banner once Declined. Nothing renders when the deal is Active.
//
// The banner pulls the full decline state (proposer / ratifier display
// names, linked decline-letter document) from GET .../decline so it can
// render attribution without a second fetch elsewhere.

import { useDeclineState } from "@/hooks/deals/useDeclineWorkflow";
import type { DealDeclineStatus } from "@/types/deals";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  dealId: string;
  declineStatus: DealDeclineStatus | undefined;
}

export function DealDeclineBanner({ dealId, declineStatus }: Props) {
  // Only fetch when there is something to show — an Active deal has no banner.
  const enabled = declineStatus === "DeclineProposed" || declineStatus === "Declined";
  const { data } = useDeclineState(enabled ? dealId : undefined);
  const { date } = useDateFormatter();

  if (!enabled || !data) return null;

  if (data.status === "DeclineProposed") {
    return (
      <div
        className="rounded-md border-l-4 border-amber-500 bg-amber-50 px-space-3 py-space-3"
        role="status"
      >
        <p className="font-semibold text-amber-900 m-0">Decline proposed</p>
        <p className="text-small text-amber-800 mt-space-1">
          {data.proposedByDisplayName ?? "A team member"} proposed declining
          this deal{data.proposedAt ? ` on ${date(data.proposedAt)}` : ""}.
        </p>
        {data.proposedReason && (
          <p className="text-small italic mt-space-2 m-0">
            "{data.proposedReason}"
          </p>
        )}
        <p className="text-caption text-amber-700 mt-space-2 m-0">
          Awaiting Loan Administrator ratification. Work on the deal
          continues until ratified.
        </p>
      </div>
    );
  }

  // status === "Declined". The lock line states what exists (owner ruling,
  // 2026-10-26): DeclinedDealReadOnlyMiddleware refuses every write but the
  // decline workflow, clone, and ADMIN's archive and restore.
  // Originally intended, in case it is built: an admin reactivating a ratified
  // decline through Admin Undo (deferred by the retired Deal Flow spec:
  // AdminUndoService.UndoDeclineRatificationAsync; #230). It was
  // never built, and Deal Pipeline spec §6.6 now says a declined deal may not be
  // reactivated; the way forward is a clone.
  return (
    <div
      className="rounded-md border-l-4 border-red-600 bg-red-50 px-space-3 py-space-3"
      role="status"
    >
      <p className="font-semibold text-red-900 m-0">Deal Declined</p>
      <p className="text-small text-red-800 mt-space-1">
        Ratified by {data.ratifiedByDisplayName ?? "the Loan Administrator"}
        {data.ratifiedAt ? ` on ${date(data.ratifiedAt)}` : ""}.
      </p>
      {data.ratifiedNotes && (
        <p className="text-small italic mt-space-2 m-0">
          "{data.ratifiedNotes}"
        </p>
      )}
      {data.linkedDeclineLetterDocumentId && (
        <a
          href={`/documents/${data.linkedDeclineLetterDocumentId}`}
          className="text-small underline mt-space-2 inline-block"
        >
          View decline letter →
        </a>
      )}
      <p className="text-caption text-red-700 mt-space-2 m-0">
        This deal is locked from further edits and cannot be reactivated. It
        can still be cloned, and only an admin can archive or restore it.
      </p>
    </div>
  );
}
